<script setup lang="ts">
import { object as vObject, safeParse as vSafeParse } from 'valibot';
import type { Link } from '@unhead/vue';
import { breakpointsTailwind } from '@vueuse/core';
import type { HeadHrefLangLink } from '@@/shared/url';
import { languages } from '@@/shared/application';
import { type TransactionType, TRANSACTION_TYPE, TransactionTypeSchema } from '@@/shared/property/types';
import { getMainTranslatedSerpTo } from '@@/shared/property/url-serp';
import { TRANSACTION_TYPE_TRANSLATIONS } from '@@/shared/property/url-referential';
import { type IadAppRoute, ErrorSchema } from '~/application';

// https://github.com/unjs/nitro/issues/470
// “error” has to be renamed, it was crashing during tests
const { params } = useRoute();
const { data: property, suspense, error: propertyError } = useFetchProperty(params.propertyListingRef);
await suspense();

const { t, locale, defaultLocale } = useI18n();
const localePath = useLocalePath();
const errorResult = vSafeParse(ErrorSchema, propertyError.value);
let isRedirecting = false;

if (!isRedirecting && errorResult.success && errorResult.output.statusMessage === `PROPERTY_NOT_FOUND`) {
  const rentalTransactionType = TRANSACTION_TYPE_TRANSLATIONS[TRANSACTION_TYPE.RENTAL][locale.value];
  const rentalRegex = new RegExp(`-${rentalTransactionType}-`, 'i');
  const redirectTransactionType = params.propertySlug != null && rentalRegex.test(String(params.propertySlug))
    ? TRANSACTION_TYPE.RENTAL
    : TRANSACTION_TYPE.SALE;
  const path = localePath(getMainTranslatedSerpTo(locale.value)[redirectTransactionType]);
  if (path) {
    isRedirecting = true;
    await navigateTo({ path }, { replace: true, redirectCode: 301 });
  };
}

// https://iad-holding.atlassian.net/browse/WEB-753
// https://iadgroup-my.sharepoint.com/:x:/g/personal/charlotte_barre_iadinternational_com/EXERqup0aAZCt88XwC-xZkkBE2YOzH0SzhfbzyZB66pm_Q?e=6XQ5Nn&wdLOR=c59AD779B-6442-C14F-A156-50E69C3BA00A
const ErrorDataSchema = vObject({
  transactionType: TransactionTypeSchema,
});
if (!isRedirecting && errorResult.success && errorResult.output.statusMessage === `DB_PROPERTY_MISSING_IAD_PROVIDER`) {
  const errorDataResult = vSafeParse(ErrorDataSchema, errorResult.output.data?.data);
  const transactionType: TransactionType = errorDataResult.success ? errorDataResult.output.transactionType : TRANSACTION_TYPE.SALE;
  const pathParams = getMainTranslatedSerpTo(locale.value)[transactionType];
  const path = localePath(pathParams);
  if (path) {
    isRedirecting = true;
    await navigateTo({ path }, { replace: true, redirectCode: 301 });
  };
}

const { title: propertyMetaTitle, pageTitle } = usePropertyMetaTitle(property);
const { title: propertyTitle } = usePropertyTitle(property);
const statusCode = computed(() => {
  if (!errorResult.success) return;
  return errorResult.output.data?.statusCode;
});
const displayError = computed(() => statusCode.value != null);

const slugs = computed(() => {
  return property.value?.slugs ?? {};
});

// Don't need to make this reactive
// Url coming from nuxt links will always be fine (coming from API slugs)
// This will only being on SSR if URL is coming from SEO or tempered in the browser URL bar
const expectedSlug = slugs.value[locale.value];
if (!isRedirecting && displayError.value === false && expectedSlug !== params.propertySlug) {
  const pathParams: IadAppRoute<'property-propertySlug-rpropertyListingRef'> = {
    name: 'property-propertySlug-rpropertyListingRef',
    params: {
      propertySlug: expectedSlug || ``,
      propertyListingRef: String(params.propertyListingRef),
    },
  };
  const path = localePath(pathParams, locale.value);
  if (path) {
    isRedirecting = true;
    await navigateTo({ path }, { replace: true, redirectCode: 301 });
  }
};

// Since Property Slug can't be translated automatically by Nuxt I18n
// We need to generate the alternate links manually
const config = useRuntimeConfig();
const headLinks = computed(() => {
  const links: Array<HeadHrefLangLink> = languages.map((lang) => {
    const pathParams: IadAppRoute<'property-propertySlug-rpropertyListingRef'> = {
      name: 'property-propertySlug-rpropertyListingRef',
      params: {
        propertySlug: slugs.value[lang] || ``,
        propertyListingRef: String(params.propertyListingRef),
      },
    };
    const href = localePath(pathParams, lang);
    return {
      id: `i18n-alt-${lang}`,
      rel: 'alternate',
      // make an absolute URL
      href: new URL(href, config.public.i18n.baseUrl).href,
      hreflang: lang,
    };
  });
  const defaultAlternate = links.find(link => link.hreflang === defaultLocale);
  if (defaultAlternate) {
    const link = { ...defaultAlternate };
    link.hreflang = 'x-default';
    link.id = 'i18n-xd';
    links.push(link);
  };
  const canonical = links.find(link => link.hreflang === locale.value);
  if (canonical) {
    const link = { ...canonical };
    link.rel = 'canonical';
    link.id = 'i18n-can';
    delete link.hreflang;
    links.push(link);
  };
  return links;
});

const i18nHead = useLocaleHead({ key: 'id' });
useHead({
  title: pageTitle.value,
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  // Lie to TS as the format follow what is returned by NuxtI18n
  link: [...(headLinks.value as Link[] || [])],
  meta: [
    ...(i18nHead.value?.meta ?? []),
    { property: 'og:type', content: `article` },
    { property: 'og:title', content: pageTitle.value },
    { name: 'description', content: property.value?.descriptionSeo },
    { property: 'og:description', content: t(`property.page_meta.description`, { title: propertyMetaTitle.value }) },
    { property: 'og:image', content: property.value?.photos[0] },
    { property: 'og:image:secure_url', content: property.value?.photos[0] },
    { property: 'og:image:width', content: 600 },
    { property: 'og:image:height', content: 600 },
    { property: 'og:image:type', content: `image/jpeg` },
  ],
});

// SHARE
const displayDesktopShare = ref(false);
function openShareDialog() {
  displayDesktopShare.value = true;
}

const breakpoints = useBreakpoints(breakpointsTailwind);
const smallScreens = breakpoints.smaller(`md`);
const { share, isSupported } = useShare();
const { title } = usePropertyMetaTitle(property);
const head = useLocaleHead({ key: `id` });
const propertyLink = computed(() => head.value.meta?.find((meta: Record<string, string>) => meta.property === `og:url`)?.content);
function startShare() {
  if (!smallScreens.value) return openShareDialog();
  if (!isSupported.value) return openShareDialog();
  share({
    title: title.value,
    url: propertyLink.value,
  });
}

// MAP
const displayMapDialog = ref(false);

// CONTACT
const displayContactForm = ref(false);
watch(displayContactForm, function trackContact(isDialogOpen) {
  if (!isDialogOpen) return;
  trackEvent({
    event: 'agent_interaction',
    event_name: 'click_to_email',
    agent_id: property.value?.agent.localId,
    property_id: property.value?.propertyListingRef,
    property_category: getTrackingPropertyCategory(property.value),
  });
});
</script>

<template>
  <div class="contents">
    <IadPlaceholderError
      v-if="statusCode"
      :status-code="statusCode"
    />
    <IadWrapper v-if="property">
      <div class="lg:grid grid-cols-[minmax(0,_1fr)_471px] gap-xl py-m max-w-screen-xl mx-auto">
        <div>
          <header>
            <PropertySectionNavigation
              :property
              @share="startShare"
            />
            <PropertySectionCover
              :property
              :title="propertyTitle"
              @share="startShare"
              @contact="displayContactForm = true"
            />
            <PropertySectionTitlesTags
              :property
              :title="propertyTitle"
              @map="displayMapDialog = true"
            />
          </header>
          <PropertySectionDescription
            :property
            @share="startShare"
          />
          <PropertySectionFeatures
            v-if="property.hasFeatures"
            :property
          />
          <PropertySectionCoOwnership
            v-if="property.hasCoOwnership"
            :property
          />
          <PropertySectionPrice :property />
          <PropertySectionDiagnostic :epc-ges="property.epcGes" />
          <PropertySectionMap
            v-if="property.location.hasMapInfo"
            v-model="displayMapDialog"
            :property
            title-tag="h2"
            @share="startShare"
            @contact="displayContactForm = true"
          />
        </div>
        <PropertySectionAgent
          v-model="displayContactForm"
          :property
        />
        <ClientOnly>
          <PropertySectionAgentFixed
            v-model="displayContactForm"
            :property
            class="lg:hidden"
          />
        </ClientOnly>
      </div>
      <PropertySectionSimilar
        v-if="property.similarItems.length"
        :similar-items="property.similarItems"
        title-tag="h3"
      />
      <ClientOnly>
        <PropertyDialogShare
          v-model="displayDesktopShare"
          :property
        />
        <AgentDialogContact
          v-model="displayContactForm"
          :agent="property.agent"
          variant="property"
          :property
        />
      </ClientOnly>
      <IadBreadcrumbs
        :items="property.breadcrumbs"
        class="py-2xl"
        text-tag="h1"
      />
    </IadWrapper>
  </div>
</template>
